import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useDicomUploadLifecycle } from '@/views/case-settings/upload/shared/useDicomUploadLifecycle';
import { createCtStudy } from '@/views/case-settings/upload/ct/createCtStudy';
import { useCaseSettings } from '@/stores/caseSettings/store';
import { useVersion } from '@/stores/version';
import type { CaseStudy } from '@/stores/caseSettings/types';
import { toSeriesRows } from '@/views/case-settings/upload/ct/seriesRow';
import { summarisePatientIdentityConflict } from '@/views/case-settings/upload/ct/patientIdentityConflict';
import type { DicomUploadLifecycleConfig } from '@/views/case-settings/upload/shared/upload.types';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';

const FILE_NAME = 'drag-n-drop upload';

type CaseSettingsStore = ReturnType<typeof useCaseSettings>;

function buildCtLifecycleConfig(
    caseSettings: CaseSettingsStore,
): DicomUploadLifecycleConfig<File, DicomInfo> {
    return {
        modality: 'ct-scan',
        requireSingleSeries: true,
        uploadType: 'ct-scan',
        extractFile: (f) => f,
        createStudy: async ({ caseId, candidates }) => {
            return createCtStudy(
                FILE_NAME,
                caseId,
                candidates[0],
                useVersion().webComponentVersion,
            );
        },
        buildWorkerPayload: ({ candidates }) => cloneDeep(candidates[0].series.items),
        onUploadComplete: ({ candidates }) => {
            caseSettings.state.case.study = {
                name: FILE_NAME,
                series: candidates[0].id,
                fileCount: candidates[0].series.items.length,
                sourceModality: 'ct-scan',
            };
        },
        messages: {
            autoUpload: 'Single series auto-uploaded',
            userConfirmed: 'Series uploaded by user',
            uploadDespiteMismatch: 'Single series confirmed by user even though manually entered patient details do not match dicom metadata',
        },
    };
}

type CtLifecycle = ReturnType<typeof useDicomUploadLifecycle<File, DicomInfo>>;

function lifecyclePassthrough(lifecycle: CtLifecycle) {
    return {
        state: lifecycle.state,
        error: lifecycle.error,
        hasUploaded: lifecycle.hasUploaded,
        progress: lifecycle.progress,
        patientIdentityCheck: lifecycle.patientIdentityCheck,
        patientComparison: lifecycle.patientComparison,
        processDicomsResult: lifecycle.processDicomsResult,
        isUploading: lifecycle.isUploading,
        isWaitingForConfirmation: lifecycle.isWaitingForConfirmation,
        isWaitingForSeriesSelection: lifecycle.isWaitingForSeriesSelection,
        isCollecting: lifecycle.isCollecting,
        isError: lifecycle.isError,
        isModalError: lifecycle.isModalError,
        isPatientDicomMismatch: lifecycle.isPatientDicomMismatch,
        hasMultiplePatientIdentities: lifecycle.hasMultiplePatientIdentities,
        patient: lifecycle.patient,
        uploadAnyway: lifecycle.uploadAnyway,
        userConfirmationUpload: lifecycle.userConfirmationUpload,
        cancel: lifecycle.cancel,
        $reset: lifecycle.reset,
    };
}

export const useCtUploadViewModel = defineStore('ct-upload-view', () => {
    const caseSettings = useCaseSettings();

    const lifecycle = useDicomUploadLifecycle<File, DicomInfo>(buildCtLifecycleConfig(caseSettings));

    const getStudy = computed((): CaseStudy | null => caseSettings.case.study);
    const hasScan = computed(() => lifecycle.hasUploaded.value || !!getStudy.value);
    const hasEnteredPatientData = computed(() => caseSettings.hasEnteredPatientData);
    const isCaseDeemedUnsuitable = computed(() => caseSettings.isCaseDeemedUnsuitable);
    const seriesRows = computed(() => toSeriesRows(lifecycle.seriesGroups.value));
    const patientIdentityConflict = computed(() =>
        summarisePatientIdentityConflict(lifecycle.state.value.patientIdentityCheck?.identities ?? []),
    );

    const selected = ref<string[]>([]);
    const hasSeries = computed(() => seriesRows.value.length > 0);
    const canContinue = computed(() => selected.value.length === 1);

    watch(
        () => lifecycle.isWaitingForSeriesSelection.value,
        (open) => {
            if (open) selected.value = [];
        },
    );

    async function confirmSeriesSelection() {
        if (!canContinue.value) return;
        await lifecycle.selectSeries(selected.value[0]);
    }

    async function addFilesAndUpload(files: File[]) {
        await lifecycle.start(files);
    }

    return {
        ...lifecyclePassthrough(lifecycle),
        // VM-owned
        hasScan,
        getStudy,
        hasEnteredPatientData,
        isCaseDeemedUnsuitable,
        seriesRows,
        patientIdentityConflict,
        selected,
        hasSeries,
        canContinue,
        confirmSeriesSelection,
        addFilesAndUpload,
    };
});
